import {
  Box,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import { SearchPartyInput } from 'components/search-party-input';
import { getLedgerListings } from 'graphql/ledger/queries';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import SearchContact from 'views/orders/new-charges/components/searchContact';

const useStyles = makeStyles((theme: any) => ({
  input: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: 'black',
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
    },
  },
  dialog: {
    padding: theme.spacing(4),
    // The footer sits below this scroll region, so the content padding alone
    // separates the last field from it; drop that field's trailing margin.
    '& > :last-child': { marginBottom: 0 },
  },
  modaltitle: {
    fontWeight: 500,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(2)
  },
  modalText: {
    color: theme.palette.common.black,
    marginBottom: theme.spacing(2)
  },
  button: {
    textTransform: 'none',
    fontWeight: 500
  },
  textbutton: { fontWeight: 500, color: theme.palette.common.black },
  containerItems: {
    marginBottom: theme.spacing(3),
    width: "100%"
  },
  containerSelectedUser: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  footer: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 4),
    // Keep actions clear of the home indicator when the dialog is full screen.
    paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
    borderTop: `1px solid ${theme.palette.neutraldivider}`,
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
  },
}));

type AddcontactModalProps = {
  open: boolean;
  onClose: () => void;
  onClickAddParty: (partyId: string, kind: string, callback: () => void) => void;
  allowedCodeAssociations: string,
  loading?: boolean,
  preselectedcontact?: {
    _id: string,
    name: string
  },
}

const AddContactModal: React.FC<AddcontactModalProps> = ({
  open,
  onClose,
  onClickAddParty,
  loading,
  allowedCodeAssociations,
  preselectedcontact
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [currentUserSelected, setCurrentUserSelected] = useState<{ _id: string, name: string } | null>(preselectedcontact || null);
  const [currentAssociation, setCurrentAssociation] = useState<string | null>(null);
  const getLedgerAllowedAssociations = useQuery(
    ['listings', allowedCodeAssociations],
    () => getLedgerListings(allowedCodeAssociations),
    {
      enabled: open,
      refetchOnWindowFocus: false
    }
  );

  const onCloseModal = () => {
    setCurrentUserSelected(null);
    setCurrentAssociation(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={onCloseModal} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogContent className={classes.dialog}>
        <Typography variant="body1" className={classes.modaltitle}>
          {t('parties:search-through')}
        </Typography>
        <Typography variant="body2" className={classes.modalText}>
          {t('parties:search-through-text')}
        </Typography>
        <Box className={classes.containerItems}>
          {preselectedcontact ?
            <Box>
              <Typography>{t('common:selected-contact')}</Typography>
              <Typography>{preselectedcontact.name}</Typography>
            </Box>
            :
            <SearchContact
              getValue={(contact) => setCurrentUserSelected(contact ? { _id: contact._id, name: contact.name } : null)}
              reference={null}
              initialValue={{ name: '', _id: '' }}
            />}
        </Box>
        <Box display="flex" flex={1} className={classes.containerItems}>
          <TextField
            select
            label={t('common:role')}
            fullWidth
            value={currentAssociation || ''}
            variant="filled"
            className={classes.input}
            onChange={(e) => {
              setCurrentAssociation(e.target.value);
            }}
          >
            <MenuItem value="">
              {t('common:select-one')}
            </MenuItem>
            {getLedgerAllowedAssociations.data?.getListing.entries.map((f) => f.description).map((j) => (
              <MenuItem key={j} value={j}>
                {j}
              </MenuItem>
            )).sort()}
          </TextField>
        </Box>
      </DialogContent>
      <Box className={classes.footer}>
        <Button className={classes.button} onClick={onCloseModal}>
          {t('parties:cancel')}
        </Button>
        <BaseButton
          disableElevation
          isLoading={loading}
          disabled={!currentUserSelected || !currentAssociation}
          text={t('parties:add-party')}
          onClick={() => {
            if (currentUserSelected && currentAssociation) {
              onClickAddParty(currentUserSelected._id, currentAssociation, onCloseModal);
            }
          }}
        />
      </Box>
    </Dialog>
  );
};

export default AddContactModal;
